import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Draggable } from '../Draggable';
import { SNAP_GUIDE_COLOR } from './SnapGuide';
import { useAnimatedTimeChange } from './useAnimatedTimeChange';

import type { DraggableData, DraggableEvent } from 'react-draggable';

type Props = {
  trackHeight: number;
  scrubToTime: (time: number) => void | Promise<void>;
};

const PLAYHEAD_DEFAULT_COLOR = 'var(--accent)';

export const Playhead: React.FC<Props> = observer(
  ({ trackHeight, scrubToTime }) => {
    const videoCreator = useVideoCreatorStore();
    const time = videoCreator.time;
    const duration = videoCreator.duration;
    const timelineScale = videoCreator.timelineScale;
    const [draggedX, setDraggedX] = React.useState(14);

    const getCurrentTime = (
      e: DraggableEvent,
      data: DraggableData,
      context: any,
    ) => {
      return Math.min(
        duration,
        Math.max(
          context.startTime + (data.x - context.startX) / timelineScale,
          0,
        ),
      );
    };

    return (
      <Draggable
        onStart={(e, data) => {
          setDraggedX(data.x);
          videoCreator.isPlayheadDragging = true;
          videoCreator.isScrubbing = true;
          return { startX: data.x, startTime: time };
        }}
        onDrag={(e, data, context) => {
          e.stopPropagation();
          setDraggedX(data.x);
          const time = getCurrentTime(e, data, context);
          videoCreator.tempDragTime = time;
          void scrubToTime(time);
        }}
        onStop={async (e, data, context) => {
          let time = getCurrentTime(e, data, context);
          videoCreator.isPlayheadDragging = false;
          videoCreator.isScrubbing = false;
          videoCreator.flushPendingIsLoadingAssets();
          videoCreator.tempDragTime = null;
          void scrubToTime(time);
        }}
      >
        {ref => (
          <div ref={ref}>
            <PlayheadInner draggedX={draggedX} height={trackHeight} />
          </div>
        )}
      </Draggable>
    );
  },
);

interface PlayheadInnerProps {
  draggedX: number;
  height: number;
}

const PlayheadInner = observer(({ draggedX, height }: PlayheadInnerProps) => {
  const videoCreator = useVideoCreatorStore();
  const forcedTime = videoCreator.smootherTime;
  const timelineScale = videoCreator.timelineScale;
  const animatedRef = React.useRef<HTMLDivElement>(null);
  const duration = videoCreator.duration;
  const [startTime, setStartTime] = React.useState(0);
  const highlightPlayhead = videoCreator.isPlayheadSnapHighlighted;
  const playheadColor = highlightPlayhead
    ? SNAP_GUIDE_COLOR
    : PLAYHEAD_DEFAULT_COLOR;

  useEffect(() => {
    setStartTime(videoCreator.snapTime(videoCreator.time) * 1000);
    animate(0);
  }, [videoCreator.isPlaying, timelineScale, forcedTime]);

  useEffect(() => {
    if (!animatedRef.current) return;

    // If dragging, use `draggedX`
    if (videoCreator.isPlayheadDragging) {
      animatedRef.current.style.left = `${draggedX}px`;
      return;
    }

    // Otherwise, use the current time
    const position = videoCreator.time * timelineScale + 14;
    animatedRef.current.style.left = `${position}px`;
  }, [
    videoCreator.time,
    timelineScale,
    videoCreator.isPlayheadDragging,
    draggedX,
    forcedTime,
  ]);

  const animate = (timestamp: number) => {
    if (!animatedRef.current || videoCreator.isPlayheadDragging) return;

    const ms = Math.min(
      videoCreator.snapTime(startTime + timestamp),
      videoCreator.snapTime(duration) * 1000,
    );

    // Only update during animation if playing
    if (videoCreator.isPlaying) {
      animatedRef.current.style.left = `${(ms / 1000) * timelineScale + 14}px`;
    }
  };

  // Only use animation when playing
  useAnimatedTimeChange(
    videoCreator.isPlaying && !videoCreator.isPlayheadDragging,
    startTime,
    msElapsed => {
      animate(msElapsed);
    },
  );

  return (
    <Main ref={animatedRef}>
      <Head $color={playheadColor} width="14" height="18" viewBox="0 0 20 24">
        <path d="M18,4.673c-0,-1.475 -1.198,-2.673 -2.673,-2.673l-10.654,0c-1.475,0 -2.673,1.198 -2.673,2.673l0,8.968l8,8.359l8,-8.359l-0,-8.968Z"></path>
      </Head>
      <Line height={height} $color={playheadColor} />
    </Main>
  );
});

const Main = styled.div
  .attrs({
    'data-testid': 'playhead-main',
  })
  .withConfig({
    displayName: 'Playhead__Main',
  })`
  position: absolute;
  z-index: 10;
  top: 0px;
  bottom: 0px;
`;

const Head = styled.svg<{ $color: string }>`
  position: absolute;
  transform: translateX(-7px);
  path {
    fill: ${props => props.$color};
  }
  cursor: ew-resize;
`;

const Line = styled.div<{ height: number; $color: string }>`
  position: absolute;
  top: 10px;
  bottom: 0px;
  width: 2px;
  transform: translateX(-1px);
  background: ${props => props.$color};
  pointer-events: none;
  height: ${props => props.height}px;
`;
